import React, { useContext, useEffect, useState } from 'react';
import { STable, STableHeader, STableRow, STableColumn } from '../documents/styled/SDocuments';
import { Flex, Box } from '@rebass/grid';
import { Icon, Header, Tabbar, Button, Input, Divider, Select, Pagination, Loading, Alert, LabeledValue } from '../../components';
import { UsersContext, ActiveUser, PendingUser } from '../../providers/UsersProvider';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ReactModal from 'react-modal';
import { GeoContext } from '../../providers/GeoProvider';
import Modal from '../../components/modal/Modal';
import CreatableSelect from 'react-select/lib/Creatable';
import { ValueType, InputActionMeta } from 'react-select/lib/types';
import { SErrorMessage, SLabel } from '../../components/input/styled/SInput';
import { SortOrder, FilterItem } from '../../utils/Search';


interface FilterFormValues {
    [index: string]: any;
    zuser_login: string;
    companies: string;
    zuser_lastname: string;
}

interface SelectOption {
    label: string;
    value: string;
}

interface EditFormValues {
    emailAddress: string;
    firstName: string;
    lastName: string;
    countryCode: string;
    role: string;
    phoneNumber: string;
    salesOrganization: string;
    customerIds: string[];
}

interface AddFormValues {
    zuser_email: string;
    zuser_firstname: string;
    zuser_lastname: string;
    zuser_country: string;
    zuser_typ: string;
    zuser_telno: string;
    zuser_salesorg_code: string;
    zuser_geoflg: string;
}

function Users() {

    ReactModal.setAppElement('#root');

    const { getPendingUsers, getActiveUsers, pendingUsers, activeUsers, usersLoading, approveUser, rejectUser, getUserById,
        totalActiveUsers, firstUrl, nextUrl, previousUrl, lastUrl, currentPage, navigateUsers, editUser, createUser, validateCustomerId, getPendingUserById } = useContext(UsersContext);
    const { countries, getCountries, salesOrganizations, getSalesOrgsForCountry } = useContext(GeoContext);
    const hasCountriesInCache = Object.keys(countries).length !== 0;

    const [userType, setUserType] = useState('active');

    const [createDialogOpen, toggleCreateDialog] = useState(false);
    const [editDialogOpen, toggleEditDialog] = useState(false);
    const [detailDialogOpen, toggleDetailDialog] = useState(false);
    const [acceptAlertOpen, toggleAcceptAlert] = useState(false);
    const [rejectAlertOpen, toggleRejectAlert] = useState(false);

    const [currentCountry, setCurrentCountry] = useState({ label: 'Select country', value: '' } as SelectOption);
    const [currentRole, setCurrentRole] = useState({ label: 'Select role', value: '' } as SelectOption);
    const [currentSalesOrganization, setCurrentSalesOrganization] = useState({ label: 'Select sales organization', value: '' } as SelectOption);
    const [currentGeoFlag, setCurrentGeoFlag] = useState({ label: 'Select geography', value: '' } as SelectOption);

    const [currentCustomerId, setCurrentCustomerId] = useState('');
    const [customerIds, setCustomerIds] = useState<SelectOption[]>([]);

    const [sortOrder, setSortOrder] = useState<SortOrder>({ label: '', order: '' });
    const [filterItems, setFilterItems] = useState<FilterItem[]>([]);

    const [selectedUser, setSelectedUser] = useState('');
    const [activeModalUser, setActiveModalUser] = useState<void | ActiveUser>({} as (ActiveUser));
    const [pendingModalUser, setPendingModalUser] = useState<void | PendingUser>({} as (PendingUser));

    const [customerIdError, invalidateCustomerId] = useState<string | null>('');

    useEffect(() => {
        if (userType === 'pending') {
            getPendingUsers();
        }
        if (userType === 'active') {
            getActiveUsers([], { label: '', order: '' } as SortOrder, 1);
        }
        if (!hasCountriesInCache) {
            getCountries();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType]);

    const editRoles = [
        {
            label: 'Customer',
            value: '1'
        },
        {
            label: 'Authority',
            value: '2'
        },
        {
            label: 'Employee',
            value: '3'
        },
        {
            label: 'Admin',
            value: '4'
        }
    ];

    const addRoles = editRoles.slice(1);

    const geoFlags = [
        {
            label: 'Sales Organization',
            value: '1'
        },
        {
            label: 'Country',
            value: '2'
        },
        {
            label: 'Region',
            value: '3'
        },
        {
            label: 'Global',
            value: '4'
        }
    ]

    const digit = new RegExp(/^[0-9]*$/);

    const UserSearchSchema = Yup.object().shape({
        zuser_login: Yup.string()
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .when(['companies', 'zuser_lastname'], {
                is: (companies: string, zuser_lastname: string) => !companies && !zuser_lastname,
                then: Yup.string().required('One of the fields is required!')
            }),
        companies: Yup.string()
            .matches(digit, 'Customer ID only contains numeric values')
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .when(['zuser_login', 'zuser_lastname'], {
                is: (zuser_login: string, zuser_lastname: string) => !zuser_login && !zuser_lastname,
                then: Yup.string().required('One of the fields is required!')
            }),
        zuser_lastname: Yup.string()
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .when(['zuser_login', 'companies'], {
                is: (zuser_login: string, companies: string) => !zuser_login && !companies,
                then: Yup.string().required('One of the fields is required!')
            }),
    }, [['zuser_login', 'companies'], ['zuser_login', 'zuser_lastname'], ['companies', 'zuser_lastname']]);

    const UserEditSchema = Yup.object().shape({
        firstName: Yup.string()
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .required('This field is required').trim(),
        lastName: Yup.string()
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .required('This field is required').trim(),
        role: Yup.string()
            .required('This field is required'),
        customerIds: Yup.array().of(Yup.object().shape({
            label: Yup.string(),
            value: Yup.string()
        })).when('role', (role: string, schema: Yup.ArraySchema<unknown>) => role === '1' ? schema.min(1, 'At least 1 customer ID') : schema),
        salesOrganization: Yup.string().when('role', (role: string, schema: Yup.StringSchema) => role !== '1' ? schema.required('Required') : schema),
        activated: Yup.bool().required()
    });

    const UserAddSchema = Yup.object().shape({
        zuser_email: Yup.string().email('Please fill in an email address').required('This field is required'),
        zuser_firstname: Yup.string()
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .required('This field is required').trim(),
        zuser_lastname: Yup.string()
            .min(2, 'Too Short!')
            .max(50, 'Too Long!')
            .required('This field is required').trim(),
        zuser_country: Yup.string()
            .required('This field is required'),
        zuser_typ: Yup.string()
            .required('This field is required'),
        zuser_salesorg_code: Yup.string()
            .required('This field is required'),
        zuser_geoflg: Yup.string()
            .required('This field is required')

    });

    const toggleOrder = (field: string) => {
        if (sortOrder.label === field) {
            sortOrder.order = sortOrder.order === '' ? '-' : '';
        } else {
            sortOrder.order = '';
            sortOrder.label = field;
        }
        setSortOrder(sortOrder);
        getActiveUsers(filterItems, sortOrder);
        // setStartedSearching(true);
    }

    const getCountryString = (countryCode: string) => {
        if (!countryCode) return '';
        const foundCountry = countries.find((country) => country.value === countryCode);
        if (!foundCountry) return '';
        return foundCountry.label.toLowerCase();
    }

    const getRole = (roleCode: string) => {
        if (!roleCode) return '';
        const foundRole = editRoles.find((role) => role.value === roleCode.toString());
        if (!foundRole) return '';
        return foundRole.label;
    }

    const PendingUsersTable = () => {
        return (
            <STable id={'pending-table'}>
                <STableHeader>
                    <tr>
                        <th className={'username'}>Username</th>
                        <th className={'country'}>Country</th>
                        <th className={'company-code'}>Company code</th>
                        <th className={'company-name'}>Company name</th>
                        <th className={'matched-company'}>Matching company</th>
                    </tr>
                </STableHeader>
                <tbody>
                    {pendingUsers.map((user, index) => {
                        return <STableRow data-row-id={user.username} key={index} onClick={() => { toggleDetailDialog(true); setSelectedUser(user.rid) }}>
                            <STableColumn className={'username'}>{user.username}</STableColumn>
                            <STableColumn className={'country'}>{getCountryString(user.countryCode)}</STableColumn>
                            <STableColumn className={'company-code'}>{user.companyCode}</STableColumn>
                            <STableColumn className={'company-name'}>{user.companyName}</STableColumn>
                            <STableColumn className={'matched-company'}>{user.matchedCompanyName}</STableColumn>
                        </STableRow>;
                    })}

                </tbody>
            </STable>
        );
    }

    const ActiveUsersTable = () => {
        return (
            <STable id={'active-table'}>
                <STableHeader>
                    <tr>
                        <th className={'username'} onClick={() => toggleOrder('zuser_login')}><Flex alignItems='center' justifyContent='space-between'><span>Username</span><Flex><Icon name='caret' width={16} height={16} color='#000066' /><Icon width={16} height={16} name='caret' color='#000066' rotation={180} /></Flex></Flex></th>
                        <th className={'country'} onClick={() => toggleOrder('zuser_country')}><Flex alignItems='center' justifyContent='space-between'><span>Country</span><Flex><Icon name='caret' width={16} height={16} color='#000066' /><Icon width={16} height={16} name='caret' color='#000066' rotation={180} /></Flex></Flex></th>
                        <th className={'customer-id'}>Customer ID</th>
                        <th className={'role'}>Role</th>
                        <th className={'activated'}>Activated</th>
                        {/* <th>Action</th> */}
                    </tr>
                </STableHeader>
                <tbody>
                    {activeUsers.map((user, index) => {
                        return <STableRow data-row-id={user.username} onClick={() => { toggleEditDialog(true); setSelectedUser(user.rid) }} key={index}>
                            <STableColumn className={'username'}>{user.username}</STableColumn>
                            <STableColumn className={'country'}>{getCountryString(user.country)}</STableColumn>
                            <STableColumn className={'customer-id'}>{user.customerIds.map((customerId) => customerId.value).join(',')}</STableColumn>
                            <STableColumn className={'role'}>{getRole(user.role)}</STableColumn>
                            <STableColumn className={'activated'}>{user.activated ? 'Yes' : 'No'}</STableColumn>
                        </STableRow>;
                    })}

                </tbody>
            </STable>
        );
    }

    const handleFieldChange = (setFieldValue: Function, fieldName: string) => {
        function update(...args: any[]) {
            const selected = args[0] as SelectOption;
            switch (fieldName) {
                case 'zuser_country':
                    setCurrentCountry(selected);
                    // here fetch sales organizations
                    getSalesOrgsForCountry(selected.value);
                    setCurrentSalesOrganization({ label: 'Select sales organization', value: '' } as SelectOption);
                    setFieldValue('zuser_salesorg_code', '');
                    break;
                case 'zuser_salesorg_code':
                    setCurrentSalesOrganization(selected);
                    break;
                case 'zuser_typ':
                    setCurrentRole(selected);
                    break;
                case 'zuser_geoflg':
                    setCurrentGeoFlag(selected);
                    break;
                default:
                    break;
            }
            setFieldValue(fieldName, selected.value);
        }
        return update;
    }

    const handleFieldBlur = (setFieldTouched: Function, fieldName: string) => {
        function update() {
            setFieldTouched(fieldName, true);
        }
        return update;
    }

    const handleCustomerIdsChange = (setFieldValue: Function) => {
        function update(...args: any[]) {
            const val = args[0] as SelectOption[];
            invalidateCustomerId(null);
            removeCustomerId(val);
            setFieldValue('customerIds', val);
        }
        return update;
    }

    const resultContainer = () => {
        if (userType === 'active') {
            if (activeUsers !== undefined && activeUsers.length === 0) {
                return <Flex alignItems='center' justifyContent='center'>
                    <Box style={{ textAlign: 'center' }}><h3>No results found</h3><p>Try checking your search criteria</p></Box>
                </Flex>;
            }
            return <ActiveUsersTable />;

        }
        else if (userType === 'pending') {
            if (activeUsers !== undefined && pendingUsers.length === 0) {
                return <Flex alignItems='center' justifyContent='center'>
                    <Box style={{ textAlign: 'center' }}><h3>No users pending for approval</h3></Box>
                </Flex>;
            }
            return <PendingUsersTable />;
        }
    }

    function getPageCount(usersCount: number) {
        return Math.ceil(usersCount / 15);
    }

    async function isValidCustomerId(customerId: string) {
        const isValid = await validateCustomerId(customerId);
        return isValid;
    }

    async function handleOnKeyDown(e: React.KeyboardEvent<HTMLElement>, setFieldValue: Function) {
        if (e.key === 'Enter' || e.key === 'Tab') {
            // supress submitting the form
            e.preventDefault();

            // don't allow empty value
            if (currentCustomerId === '') {
                return;
            }

            const isValid = await isValidCustomerId(currentCustomerId);

            if (!isValid) {
                invalidateCustomerId('This customer ID is not valid');
                return;
            };

            invalidateCustomerId(null);
            const option = { label: currentCustomerId, value: currentCustomerId } as SelectOption;
            // don't allow already existing customer id
            if (!customerIds.find((element) => element.label === option.label)) {
                addCustomerId(option);
                setCurrentCustomerId('');
                setFieldValue('customerIds', [...customerIds, option]);
            }
        }
    }

    function addCustomerId(value: SelectOption) {
        const cloned = customerIds.slice(0);
        cloned.push(value);
        setCustomerIds(cloned);
    }

    function removeCustomerId(ids: ValueType<SelectOption[]>) {
        const idList = ids as SelectOption[];
        setCustomerIds(idList);
    }

    function createFilterItems(values: FilterFormValues) {
        const items: FilterItem[] = [];
        Object.keys(values).forEach((key) => {
            let item: FilterItem = { key: '', value: '' };
            item.key = key;
            if (typeof values[key] === 'object') {
                item.value = values[key].value;
            } else {
                item.value = values[key];
            }
            items.push(item);
        });
        return items;
    }

    function getSelectedRole(roles: SelectOption[], values: ActiveUser) {
        return roles.find((role) => role.value.toString() === values.role.toString())
    }

    function getSelectedGeoFlag(geoFlags: SelectOption[], values: ActiveUser) {
        return geoFlags.find((geoFlag) => geoFlag.value.toString() === values.geoFlag.toString())
    }

    function getSelectedSalesOrganization(salesOrganizations: SelectOption[], values: ActiveUser) {
        return salesOrganizations.find((so) => so.value === values.salesOrganization);
    }

    function handleApprove() {
        approveUser((pendingModalUser as PendingUser).rid);
        setPendingModalUser({} as PendingUser);
        toggleAcceptAlert(false);
    }

    function handleReject() {
        rejectUser((pendingModalUser as PendingUser).rid);
        setPendingModalUser({} as PendingUser);
        toggleRejectAlert(false);
    }

    function pendingUser() {
        return pendingModalUser as PendingUser;
    }

    function parseDate(createdAt: string) {
        const date = new Date(createdAt);
        return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()} at ${date.getHours()}:${date.getMinutes()}`;
    }

    return (
        <React.Fragment>

            {/* Accept alert */}
            <Alert onCancel={() => toggleAcceptAlert(false)} onAccept={handleApprove} acceptText="approve" cancelText="cancel" confirmButtonId={'approve'} isOpen={acceptAlertOpen}>
                Are you sure you want to approve this user?
            </Alert>

            {/* Reject alert */}
            <Alert onCancel={() => toggleRejectAlert(false)} onAccept={handleReject} acceptText="reject" cancelText="cancel" confirmButtonId={'reject'} isOpen={rejectAlertOpen}>
                Are you sure you want to reject/remove this user?
            </Alert>

            {/* Add user dialog */}
            <Modal testId={'add-user'} width={660} height={560} isOpen={createDialogOpen} onRequestClose={() => toggleCreateDialog(false)}>
                <Header>Add user</Header>
                <Formik
                    initialValues={{
                        zuser_email: '',
                        zuser_firstname: '',
                        zuser_lastname: '',
                        zuser_country: '',
                        zuser_typ: '',
                        zuser_telno: '',
                        zuser_salesorg_code: '',
                        zuser_geoflg: ''
                    }}
                    validationSchema={UserAddSchema}
                    onSubmit={(values: AddFormValues) => {
                        // setStartedSearching(true);
                        createUser(values);
                        if (!usersLoading) {
                            toggleCreateDialog(false);
                        }
                    }}
                    render={({ touched, errors, setFieldValue, setFieldTouched }) => (
                        <Box>
                            <Form method="post">
                                <Flex justifyContent='space-between' mt={30} >
                                    <Box width='100%'>
                                        <Input required={true} touched={touched.zuser_firstname} error={errors.zuser_firstname} id="firstName" name="zuser_firstname" label="First name" type="text" placeholder="Enter first name" />
                                    </Box>
                                    <Box width={80}></Box>
                                    <Box width='100%'>
                                        <Input required={true} touched={touched.zuser_lastname} error={errors.zuser_lastname} id="lastName" name="zuser_lastname" label="Last name" type="text" placeholder="Enter last name" />
                                    </Box>
                                </Flex>
                                <Flex justifyContent='space-between' mt={30} >
                                    <Box width='100%'>
                                        <Input required={true} touched={touched.zuser_email} error={errors.zuser_email} id="emailAddress" name="zuser_email" label="Email" type="text" placeholder="Enter email address" />
                                    </Box>
                                    <Box width={80}></Box>
                                    <Box width='100%'>
                                        <Input id="phoneNumber" name="zuser_telno" label="Phone number" type="text" placeholder="Enter phone number" />
                                    </Box>
                                </Flex>
                                <Flex justifyContent='space-between' mt={30} >
                                    <Box width='100%'>
                                        <Select required={true} touched={touched.zuser_country} error={errors.zuser_country} name="zuser_country" onChange={handleFieldChange(setFieldValue, 'zuser_country')} onBlur={handleFieldBlur(setFieldTouched, 'zuser_country')} options={countries} value={currentCountry} id="input-country" label="Country" placeholder="Select country" />
                                    </Box>
                                    <Box width={80}></Box>
                                    <Box width='100%'>
                                        <Select required={true} touched={touched.zuser_typ} error={errors.zuser_typ} name="zuser_typ" onChange={handleFieldChange(setFieldValue, 'zuser_typ')} onBlur={handleFieldBlur(setFieldTouched, 'zuser_typ')} options={addRoles} value={currentRole} id="input-role" label="Role" placeholder="Select role" />
                                    </Box>
                                </Flex>
                                <Flex justifyContent='space-between' mt={30} >
                                    <Box width='100%'>
                                        <Select required={true} touched={touched.zuser_salesorg_code} error={errors.zuser_salesorg_code} name="zuser_salesorg_code" onChange={handleFieldChange(setFieldValue, 'zuser_salesorg_code')} onBlur={handleFieldBlur(setFieldTouched, 'zuser_salesorg_code')} options={salesOrganizations} value={currentSalesOrganization} id="input-sales-organization" label="Sales organization" placeholder="Select sales organization" />
                                    </Box>
                                    <Box width={80}></Box>
                                    <Box width='100%'>
                                        <Select required={true} touched={touched.zuser_geoflg} error={errors.zuser_geoflg} name="zuser_geflg" onChange={handleFieldChange(setFieldValue, 'zuser_geoflg')} onBlur={handleFieldBlur(setFieldTouched, 'zuser_geoflg')} options={geoFlags} value={currentGeoFlag} id="input-geo-flag" label="Geography" placeholder="Select geography" />
                                    </Box>
                                </Flex>

                                <Box mt={30}>
                                    <Divider />
                                </Box>
                                <Flex mt={30} justifyContent='flex-end'>
                                    <Box>
                                        <Button id="button-add-user" kind='primary' type='submit' loading={usersLoading}>save</Button>
                                    </Box>
                                </Flex>

                            </Form>
                        </Box>
                    )}
                />
            </Modal>
            {/* End of add user modal */}

            {/*  Edit user form  */}
            <Modal testId={'edit-user'} onAfterOpen={() => getUserById(selectedUser).then((user) => {
                setActiveModalUser(Object.assign(activeModalUser, user));
                getSalesOrgsForCountry((user as ActiveUser).country);
                setCustomerIds((user as ActiveUser).customerIds);
            })} width={660} height={560} isOpen={editDialogOpen} loading={usersLoading} onRequestClose={() => { setCustomerIds([]); setActiveModalUser({} as ActiveUser); toggleEditDialog(false) }}>
                {(activeModalUser as ActiveUser).rid ? (
                    <React.Fragment><Header>Edit user</Header>
                        <Formik
                            initialValues={(activeModalUser as ActiveUser)}
                            validationSchema={UserEditSchema}
                            onSubmit={(values: ActiveUser) => {
                                if (values.role === '1') {
                                    Object.assign(values.customerIds, customerIds);
                                }
                                editUser(values);
                                if (!usersLoading) {
                                    toggleEditDialog(false);
                                }
                            }}
                            render={({ touched, errors, setFieldValue, setFieldTouched, values }) => (
                                <Box>
                                    <Form method="post">
                                        <Flex justifyContent='space-between'>
                                            <Box mt={30} width='100%'>
                                                <LabeledValue label="Username" id="username" value={values.username} />
                                            </Box>
                                            <Box width={80}></Box>
                                            <Box mt={30} width='100%'>
                                                <LabeledValue label="Email address" id="emailAddress" value={values.emailAddress} />
                                            </Box>
                                        </Flex>
                                        <Flex justifyContent='space-between'>
                                            <Box mt={30} width='100%'>
                                                <Input required={true} touched={touched.firstName} error={errors.firstName} id="firstName" name="firstName" label="First name" type="text" placeholder="Enter first name" value={values.firstName} />
                                            </Box>
                                            <Box width={80}></Box>
                                            <Box mt={30} width='100%'>
                                                <Input required={true} touched={touched.lastName} error={errors.lastName} id="lastName" name="lastName" label="Last name" type="text" placeholder="Enter last name" value={values.lastName} />
                                            </Box>
                                        </Flex>
                                        <Flex justifyContent='space-between'>

                                            <Box mt={30} width='100%'>
                                                <Input id="phoneNumber" name="phoneNumber" label="Phone number" type="text" placeholder="Enter phone number" value={values.phoneNumber} />
                                            </Box>
                                            <Box width={80}></Box>
                                            <Box mt={30} width='100%'>
                                                {values.role.toString() !== '1' && <Select required={true} touched={touched.role} error={errors.role} name="role" onChange={handleFieldChange(setFieldValue, 'role')} onBlur={handleFieldBlur(setFieldTouched, 'role')} options={addRoles} value={getSelectedRole(addRoles, values)} id="input-role" label="Role" placeholder="Select role" />}
                                                {values.role.toString() === '1' && <LabeledValue label="Role" id="role" value={getRole(values.role)} />}
                                            </Box>
                                        </Flex>
                                        <Flex justifyContent='space-between'>
                                            {values.role.toString() === '1' && <Box mt={30} width='100%'>
                                                <SLabel>Customer IDs <span className="asterisk">*</span></SLabel>
                                                <CreatableSelect theme={(theme) => ({
                                                    ...theme,
                                                    spacing: {
                                                        menuGutter: 0,
                                                        baseUnit: theme.spacing.baseUnit,
                                                        controlHeight: theme.spacing.controlHeight
                                                    },
                                                    colors: {
                                                        ...theme.colors,
                                                        primary: '#000066'
                                                    },
                                                    borderRadius: 0
                                                })} styles={{
                                                    control: (base, state) => ({
                                                        ...base,
                                                        border: (errors.customerIds || customerIdError) ? '1px solid #cc0033' : '1px solid #777',
                                                        padding: state.isMulti ? '4px 6px' : '8px 6px',
                                                        boxShadow: 'none',
                                                        fontSize: '13px',
                                                        minHeight: '45px',
                                                        backgroundColor: (errors.customerIds || customerIdError) ? '#fffafb;' : 'white',
                                                        '&:hover': { borderColor: '#777' },
                                                        'svg': { color: (errors.customerIds || customerIdError) ? '#cc0033' : '#777' }
                                                    })
                                                }}
                                                    components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                                                    inputValue={currentCustomerId}
                                                    isClearable
                                                    isMulti={true}
                                                    menuIsOpen={false}
                                                    onChange={handleCustomerIdsChange(setFieldValue)}
                                                    onInputChange={(value: string, actionMeta: InputActionMeta) => setCurrentCustomerId(value)}
                                                    onKeyDown={(e) => {
                                                        e.persist();
                                                        handleOnKeyDown(e, setFieldValue);
                                                    }}
                                                    onBlur={handleFieldBlur(setFieldTouched, 'customerIds')}
                                                    placeholder="Type customer ID and press ENTER"
                                                    name="customerIds"
                                                    touched={touched.customerIds}
                                                    value={customerIds}
                                                />
                                                {(errors.customerIds || customerIdError) && <SErrorMessage id={"customerIds-error"}>{errors.customerIds || customerIdError}</SErrorMessage>}
                                            </Box>}
                                            {values.role.toString() === '1' && <React.Fragment><Box width={80}></Box><Box width='100%'></Box></React.Fragment>}

                                        </Flex>
                                        {values.role.toString() !== '1' && <Flex justifyContent='space-between'>
                                            <Box mt={30} width='100%'>
                                                <Select required={true} touched={touched.salesOrganization} error={errors.salesOrganization} name="salesOrganization" onChange={handleFieldChange(setFieldValue, 'salesOrganization')} onBlur={handleFieldBlur(setFieldTouched, 'salesOrganization')} options={salesOrganizations} value={getSelectedSalesOrganization(salesOrganizations, values)} id="input-sales-organization" label="Sales organization" placeholder="Select sales organization" />

                                            </Box>
                                            <Box width={80}></Box>
                                            <Box mt={30} width='100%'>
                                                <Select required={true} touched={touched.geoFlag} error={errors.geoFlag} name="geoFlag" onChange={handleFieldChange(setFieldValue, 'geoFlag')} onBlur={handleFieldBlur(setFieldTouched, 'geoFlag')} options={geoFlags} value={getSelectedGeoFlag(geoFlags, values)} id="input-geo-flag" label="Geography" placeholder="Select geography" />
                                            </Box>
                                        </Flex>}
                                        <Flex justifyContent='space-between'>
                                            <Box mt={30} width='100%'>
                                                <Input touched={touched.comment} error={errors.comment} id="comment" name="comment" label="Comment" type="text" placeholder="Enter comment" value={values.comment} />
                                            </Box>
                                            <Box width={80}></Box>
                                            <Box mt={30} width='100%'>
                                                <LabeledValue label="Country" id="country" value={getCountryString(values.country)} />
                                            </Box>
                                        </Flex>
                                        <Box mt={30}>
                                            <Divider />
                                        </Box>
                                        <Flex mt={30} justifyContent='space-between' alignItems="center">
                                            <Box>
                                                <input type='checkbox' name='activated' checked={values.activated} onChange={(e: React.ChangeEvent<Element>) => setFieldValue('activated', !values.activated)} /> Active
                                            </Box>
                                            <Box>
                                                <Button id="button-edit-user" kind='primary' type='submit' loading={usersLoading}>save</Button>
                                            </Box>
                                        </Flex>

                                    </Form>
                                </Box>
                            )}
                        /></React.Fragment>) : null}
            </Modal>

            {/* User detail modal */}
            <Modal testId={'pending-user'} onAfterOpen={() => getPendingUserById(selectedUser).then((user) => {
                setPendingModalUser(user);
            })} width={660} height={480} isOpen={detailDialogOpen} loading={usersLoading} onRequestClose={() => { setPendingModalUser({} as PendingUser); toggleDetailDialog(false) }}>
                {pendingUser().rid ? (
                    <React.Fragment>
                        <Header>User detail</Header>
                        <Formik initialValues={pendingUser()} onSubmit={() => { }}
                            render={({ values }) => (
                                <Form method="post">
                                    <Flex justifyContent='space-between'>

                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Username" id="username" value={values.username} />
                                        </Box>
                                        <Box width={80}></Box>
                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Email address" id="emailAddress" value={values.username} />
                                        </Box>
                                    </Flex>
                                    <Flex justifyContent='space-between'>
                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="First name" id="firstName" value={values.firstName} />
                                        </Box>
                                        <Box width={80}></Box>
                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Last name" id="lastName" value={values.lastName} />
                                        </Box>
                                    </Flex>
                                    <Flex justifyContent='space-between'>

                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Company code" id="companyCode" value={values.companyCode} />
                                        </Box>
                                        <Box width={80}></Box>
                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Company name" id="companyName" value={values.companyName} />
                                        </Box>
                                    </Flex>
                                    <Flex justifyContent='space-between'>

                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Application date & time" id="createdAt" value={parseDate(values.createdAt)} />
                                        </Box>
                                        <Box width={80}></Box>
                                        <Box mt={30} width='100%'>
                                            <LabeledValue label="Country" id="country" value={getCountryString(values.countryCode)} />
                                        </Box>
                                    </Flex>
                                    <Box mt={30}>
                                        <Divider />
                                    </Box>
                                    <Flex mt={30} justifyContent='flex-end'>
                                        <Flex justifyContent='space-between' width='30%'>
                                            <Button disabled={!(pendingModalUser as PendingUser).acceptable} onClick={() => { toggleDetailDialog(false); toggleAcceptAlert(true) }} small={true} kind='secondary' id={`button-approve`} loading={usersLoading}>Approve</Button>
                                            <Button onClick={() => { toggleDetailDialog(false); toggleRejectAlert(true) }} small={true} kind='tertiary' id={`button-reject`} loading={usersLoading}>Reject</Button>
                                        </Flex>
                                    </Flex>
                                </Form>
                            )}
                        />
                    </React.Fragment>) : null}
            </Modal>

            {/* users and filter */}
            <Flex data-page-id="users" px={30} style={{ minWidth: '1280px', paddingBottom: '30px' }}>
                {userType === 'active' && <Box id="box-filters" width={1 / 5} p={30} mr={30} bg='white' style={{ minHeight: '400px', boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.05)' }}>
                    <Header>Filter</Header>
                    <Formik
                        initialValues={{
                            zuser_login: '',
                            companies: '',
                            zuser_lastname: ''
                        }}
                        validationSchema={UserSearchSchema}
                        onSubmit={(values: FilterFormValues) => {
                            // setStartedSearching(true);
                            setFilterItems(createFilterItems(values));
                            getActiveUsers(createFilterItems(values), sortOrder);
                        }}
                        render={({ touched, errors, resetForm, values }) => (
                            <Box>
                                <Form method="post">
                                    <Box mt={30}>
                                        <Input touched={touched.companies} error={errors.companies} id="input-filter-company-id" name="companies" label="Search by customer ID" type="text" placeholder="Enter customer ID" icon="search" value={values.companies} />
                                    </Box>
                                    <Box mt={30}>
                                        <Input touched={touched.zuser_login} error={errors.zuser_login} id="input-filter-username" name="zuser_login" label="Search by username" type="text" placeholder="Enter username" icon="search" value={values.zuser_login} />
                                    </Box>
                                    <Box mt={30}>
                                        <Input touched={touched.zuser_lastname} error={errors.zuser_lastname} id="input-filter-last-name" name="zuser_lastname" label="Search by last name" type="text" placeholder="Enter lastname" icon="search" value={values.zuser_lastname} />
                                    </Box>
                                    <Box mt={30}>
                                        <Box>
                                            <Button id="button-search" fullWidth={true} kind='primary' type='submit'>search</Button>
                                        </Box>
                                        <Box mt={15}>
                                            <Button id="button-clear" fullWidth={true} kind='secondary' type='button' onClick={() => {
                                                resetForm();
                                                getActiveUsers([], { label: '', order: '' }, 1);
                                            }}>Clear all </Button>
                                        </Box>
                                    </Box>

                                </Form>
                            </Box>
                        )}
                    />
                </Box>}

                <Box id={'box-users'} width={userType === 'active' ? 'calc(80% - 30px)' : '100%'}>
                    <Box>
                        <Tabbar onChange={(status) => setUserType(status)} items={[{ id: 'active', title: 'Users', active: userType === 'active', value: 'active' }, { id: 'pending', title: 'Pending users', active: userType === 'pending', value: 'pending' }]} />
                    </Box>
                    <Box id="box-results" p={15} pt={30} bg='white' style={{ boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.05)' }}>
                        {usersLoading ? <Flex justifyContent="center" style={{ height: "100%" }} alignItems="center">
                            <Loading width={32} height={32} />
                        </Flex> : <React.Fragment>

                                {userType === 'active' ? <Flex justifyContent='space-between'>
                                    <Header bordered={false}>Users</Header>
                                    <Button onClick={() => toggleCreateDialog(true)} small={true} id='add-user-button' kind='primary'>add user</Button>
                                </Flex> : <Header bordered={false}>Pending users</Header>}
                                {resultContainer()}
                            </React.Fragment>}
                    </Box>
                </Box>
            </Flex>
            {userType === 'active' && activeUsers.length > 0 && totalActiveUsers > 15 && <Flex px={30} >
                <Box width={1 / 5} mr={30} ></Box>
                <Flex width={'calc(80% - 30px)'} p={15} justifyContent='center' >
                    <Pagination currentPage={currentPage} pages={getPageCount(totalActiveUsers)} firstUrl={firstUrl} lastUrl={lastUrl} previousUrl={previousUrl} nextUrl={nextUrl}
                        onFirst={() => navigateUsers(firstUrl, 1)} onLast={() => navigateUsers(lastUrl, getPageCount(totalActiveUsers))}
                        onPrevious={() => navigateUsers(previousUrl, currentPage - 1)}
                        onNext={() => navigateUsers(nextUrl, currentPage + 1)}
                        onPage={navigateUsers} />
                </Flex>
            </Flex>}
        </React.Fragment>
    );
}

export default Users;
